import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ConversationAgentRoleDefaultEntity } from './conversation-agent-role-default.entity';
import { ConversationAgentToolEntity } from './conversation-agent-tool.entity';
import { ConversationAnalyzerEntity } from './conversation-analyzer.entity';
import { ConversationVoiceProfilesEntity } from './conversation-voice-profiles.entity';

/**
 * Shape of `ConversationAgentEntity.metadata.publicAccess` — configuration for
 * the public, embeddable widget key. Stored inside the agent's `metadata` JSON
 * rather than as dedicated columns so the public-access policy can grow without
 * schema churn.
 */
export interface ConversationAgentPublicAccessModel {
	/**
	 * Origins permitted to use the agent's public key. REQUIRED for the key to
	 * work — an empty or missing list fails closed (the key is inert). Supports
	 * exact origins and `*` wildcards (e.g. `https://*.example.com`).
	 */
	allowedOrigins?: Array<string>;
}

@Entity('_conversation_agent')
export class ConversationAgentEntity extends BaseEntity {
	/**
	 * When set, this agent is a system agent whose runtime behavior (prompt, tools, LLM)
	 * comes from the code-based SystemAgentDefinition, not the database record.
	 * The DB record exists for FK integrity only.
	 */
	@Column({
		name: 'systemAgentType',
		type: 'varchar',
		length: 100,
		nullable: true,
		unique: true,
	})
	@Index('idx_conversation_agent_system_type')
	public systemAgentType?: string | null;

	/**
	 * Public, discoverable key part for embedding this agent in an external
	 * website widget. When non-null, the agent is reachable via a public key of
	 * the form `wkda_<tenantAlias>_<publicApiKey>` — this column stores only the
	 * random part; the alias lives in the key string so the tenant DB can be
	 * resolved before context is set. It is an identifier, NOT a secret (it ships
	 * in page source); origin allow-listing in `metadata.publicAccess` is the
	 * defense. Null = the agent is not publicly embeddable.
	 */
	@Column({
		name: 'publicApiKey',
		type: 'varchar',
		length: 64,
		nullable: true,
		unique: true,
	})
	@Index('idx_conversation_agent_public_api_key')
	public publicApiKey?: string | null;

	@Column({ name: 'name', type: 'varchar', length: 100, nullable: false })
	public name!: string;

	@Column({ name: 'agent_type', type: 'varchar', length: 50, nullable: false })
	public agentType!: string;

	@Column({ name: 'fa_icon', type: 'varchar', length: 50, nullable: true })
	public faIcon?: string;

	@Column({ name: 'agent_color', type: 'varchar', length: 7, nullable: true })
	public agentColor?: string;

	@Column({ name: 'description', type: 'text', nullable: true })
	public description?: string;

	@Column({
		name: '_fk_conversation_voice_profile__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public voiceProfileId?: number;

	@Column({ name: 'first_message', type: 'text', nullable: false })
	public firstMessage!: string;

	@Column({ name: 'prompt', type: 'text', nullable: false })
	public prompt!: string;

	@Column({ name: 'is_active', type: 'tinyint', width: 1, default: 1 })
	public isActive!: boolean;

	@Column({ name: 'debug_mode', type: 'tinyint', width: 1, default: 0 })
	public debugMode!: boolean;

	/**
	 * When true, the AI voice path's TwiML response includes a
	 * `<Connect record="record-from-answer" ...>` directive so Twilio
	 * captures the call audio. The recording-status webhook then
	 * persists the recording sid + URL onto `_conversation_call.metadata.recording`,
	 * and the inbox bubble surfaces a play button that streams via
	 * the AI recording proxy endpoint (parity with softphone calls).
	 *
	 * Defaults to true: recording was unconditional before this column
	 * existed, so the column-add must not silently disable it for any
	 * agent. Tenants/admins can flip it off per-agent for sensitive
	 * use cases.
	 */
	@Column({
		name: 'record_calls',
		type: 'tinyint',
		width: 1,
		default: 1,
	})
	public recordCalls!: boolean;

	@Column({
		name: 'llm_provider',
		type: 'varchar',
		length: 20,
		default: "'claude'",
	})
	public llmProvider!: string;

	@Column({ name: 'llm_model', type: 'varchar', length: 100, nullable: true })
	public llmModel?: string;

	@Column({
		name: 'voice_provider',
		type: 'varchar',
		length: 50,
		nullable: true,
		comment: 'Voice provider used (elevenlabs, openai, etc.)',
	})
	public voiceProvider?: string;

	@Column({
		name: 'voice_model',
		type: 'varchar',
		length: 100,
		nullable: true,
		comment: 'Specific voice model used (eleven_turbo_v2_5, tts-1, etc.)',
	})
	public voiceModel?: string;

	@Column({
		name: 'stt_provider',
		type: 'varchar',
		length: 50,
		nullable: true,
		comment: 'STT provider used (deepgram, etc.)',
	})
	public sttProvider?: string;

	@Column({
		name: 'stt_model',
		type: 'varchar',
		length: 100,
		nullable: true,
		comment: 'Specific STT model used (nova-2, nova-3-general, etc.)',
	})
	public sttModel?: string;

	@Column({
		name: 'stt_language',
		type: 'varchar',
		length: 20,
		nullable: true,
		default: "'en'",
		comment: 'Language code for STT (en, es, multi, etc.)',
	})
	public sttLanguage?: string;

	@Column({
		name: 'stt_settings',
		type: 'json',
		nullable: true,
		comment: 'Provider-specific STT settings (e.g., Flux eager thresholds)',
	})
	public sttSettings?: Record<string, unknown>;

	@Column({
		name: 'fk__conversation_analyzer__id',
		type: 'int',
		unsigned: true,
		nullable: true,
		comment: 'Performance analyzer linked to this agent',
	})
	public analyzerId?: number;

	@ManyToOne(() => ConversationAnalyzerEntity, { nullable: true })
	@JoinColumn({ name: 'fk__conversation_analyzer__id' })
	public analyzer?: ConversationAnalyzerEntity;

	@Column({
		name: 'conversation_count',
		type: 'int',
		unsigned: true,
		default: 0,
	})
	public conversationCount!: number;

	@Column({ name: 'last_used', type: 'timestamp', nullable: true })
	public lastUsed?: Date;

	@Column({ name: 'metadata', type: 'json', nullable: true })
	public metadata?: any;

	@Column({
		name: 'monthlyMinuteLimit',
		type: 'decimal',
		precision: 10,
		scale: 2,
		nullable: true,
		default: null,
		comment:
			'Monthly voice minute limit for this agent (soft limit, nullable = no limit)',
	})
	public monthlyMinuteLimit?: number | null;

	// Relationships
	@ManyToOne(() => ConversationVoiceProfilesEntity, { nullable: true })
	@JoinColumn({ name: '_fk_conversation_voice_profile__id' })
	public voiceProfile?: ConversationVoiceProfilesEntity;

	@OneToMany(() => ConversationAgentToolEntity, (agentTool) => agentTool.agent)
	public agentTools?: ConversationAgentToolEntity[];

	@Column({
		name: 'is_chat_widget_default',
		type: 'tinyint',
		width: 1,
		default: 0,
		comment: 'Instance-wide default chat widget agent',
	})
	public isChatWidgetDefault!: boolean;

	@OneToMany(
		() => ConversationAgentRoleDefaultEntity,
		(roleDefault) => roleDefault.agent,
	)
	public roleDefaults?: Array<ConversationAgentRoleDefaultEntity>;

	// Indexes
	@Index('idx_agent_type')
	public static agentTypeIndex: string = 'agent_type';

	@Index('idx_voice_profile')
	public static voiceProfileIndex: string =
		'_fk_conversation_voice_profile__id';

	@Index('idx_owner')
	public static ownerIndex: string = '_owner_userId';

	@Index('idx_active_owner', ['is_active', '_owner_userId'])
	public static activeOwnerIndex: [string, string] = [
		'is_active',
		'_owner_userId',
	];
}
